import React, { useState } from "react";
import { Mail, Phone, MapPin, Camera, ArrowRight, Heart } from "lucide-react";
import { FaFacebookF, FaInstagram, FaWhatsapp } from "react-icons/fa";

const Footer = () => {
  const [hoveredLink, setHoveredLink] = useState(null);

  const quickLinks = [
    { name: "الرئيسية", href: "#home" },
    { name: "الأعمال", href: "#portfolio" },
    { name: "من أنا", href: "#about" },
    { name: "الخدمات", href: "#services" },
    { name: "تواصل معنا", href: "#contact" },
  ];

  const contactInfo = [
    { icon: Mail, text: "info@example.com", href: "mailto:info@example.com" },
    { icon: Phone, text: "+20 123 456 789", href: "tel:+20123456789" },
    { icon: MapPin, text: "أسوان - مركز إدفو", href: "#" },
  ];

  const socialLinks = [
    { 
      icon: FaFacebookF, 
      href: "https://www.facebook.com/kemodaana22", 
      color: "from-blue-600 to-blue-700",
      hoverBg: "hover:bg-blue-600",
      label: "Facebook"
    },
    { 
      icon: FaInstagram, 
      href: "https://www.instagram.com/Kemaa111", 
      color: "from-pink-500 to-purple-600",
      hoverBg: "hover:bg-gradient-to-br hover:from-pink-500 hover:to-purple-600",
      label: "Instagram"
    },
    { 
      icon: FaWhatsapp, 
      href: "https://wa.me/20123456789", 
      color: "from-green-500 to-emerald-600",
      hoverBg: "hover:bg-green-500",
      label: "WhatsApp"
    },
  ];

  return (
    <footer className="relative bg-gradient-to-b from-gray-900 to-black text-white overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }} />
      </div>

      {/* Gradient Orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Main Footer Content */}
        <div className="pt-16 pb-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12" dir="rtl">
          
          {/* Logo & Description */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl flex items-center justify-center shadow-lg">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-black tracking-tight">KEMO</h3>
                <p className="text-gray-400 text-xs">PHOTOGRAPHY</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              تصوير احترافي للبورتريه، حفلات الزفاف، المناسبات، والأزياء. نوثق لحظاتك بأجمل صورة.
            </p>
            
            {/* Newsletter */}
            <div className="pt-2">
              <p className="text-sm text-gray-400 mb-3">اشترك في نشرتنا البريدية</p>
              <div className="flex gap-2">
                <input 
                  type="email" 
                  placeholder="بريدك الإلكتروني"
                  className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-white/20 transition-colors"
                />
                <button className="px-4 py-2 bg-gradient-to-r from-gray-700 to-gray-800 rounded-xl hover:from-gray-600 hover:to-gray-700 transition-all duration-300 hover:scale-105">
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold mb-6 flex items-center gap-2">
              روابط سريعة
              <div className="h-px flex-1 bg-gradient-to-l from-white/20 to-transparent" />
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link, idx) => (
                <li key={idx}>
                  <a 
                    href={link.href}
                    className="group flex items-center gap-2 text-gray-400 hover:text-white transition-all duration-300"
                    onMouseEnter={() => setHoveredLink(idx)}
                    onMouseLeave={() => setHoveredLink(null)}
                  >
                    <ArrowRight 
                      className={`w-4 h-4 transition-all duration-300 ${
                        hoveredLink === idx ? 'translate-x-1 opacity-100' : 'translate-x-0 opacity-0'
                      }`}
                    />
                    <span className="group-hover:translate-x-1 transition-transform duration-300">
                      {link.name}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold mb-6 flex items-center gap-2">
              تواصل معنا
              <div className="h-px flex-1 bg-gradient-to-l from-white/20 to-transparent" />
            </h4>
            <ul className="space-y-4">
              {contactInfo.map((contact, idx) => {
                const Icon = contact.icon;
                return (
                  <li key={idx}>
                    <a 
                      href={contact.href}
                      className="group flex items-center gap-3 text-gray-400 hover:text-white transition-all duration-300"
                    >
                      <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center group-hover:bg-white/10 transition-all duration-300 group-hover:scale-110">
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="text-sm">{contact.text}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Social Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold mb-6 flex items-center gap-2">
              تابعنا على
              <div className="h-px flex-1 bg-gradient-to-l from-white/20 to-transparent" />
            </h4>
            <div className="space-y-3">
              {socialLinks.map((social, idx) => {
                const Icon = social.icon;
                return (
                  <a
                    key={idx}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`group flex items-center gap-4 bg-white/5 hover:bg-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-3 transition-all duration-500 hover:scale-105 hover:border-white/20 ${social.hoverBg}`}
                  >
                    <div className={`w-10 h-10 bg-gradient-to-br ${social.color} rounded-lg flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-grow">
                      <p className="text-white font-medium text-sm">{social.label}</p>
                      <p className="text-gray-400 text-xs">تابعنا الآن</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <span>© {new Date().getFullYear()}</span>
              <span className="text-white font-bold">KEMO PHOTOGRAPHY</span>
              <span>جميع الحقوق محفوظة</span>
            </div>
            
            <div className="flex items-center gap-2">
              <span>صُنع بـ</span>
              <Heart className="w-4 h-4 text-red-500 animate-pulse" />
              <span>في مصر</span>
            </div>
          </div>
        </div>
      </div>

      {/* Animated Shine Effect */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
    </footer>
  );
};

export default Footer;